import * as React from "react"
import { useCallback, useEffect, useId, useRef } from "react"
import { cn } from "../../lib/cn"
import { useDismiss } from "../../hooks/useDismiss"
import { useRovingActiveIndex } from "../../hooks/useRovingActiveIndex"

export interface ComboboxOption<T> {
    id: string
    value: T
    disabled?: boolean
}

export interface ComboboxInputRenderProps {
    /** Attach to the <input> so dismissal + Escape can find the trigger. */
    ref: React.Ref<HTMLInputElement>
    /** Current popup state — render a rotating chevron etc. from this. */
    open: boolean
    /** Spread onto the <input> for the combobox ARIA wiring + keyboard handler. */
    getInputProps: () => React.InputHTMLAttributes<HTMLInputElement>
}

export interface ComboboxProps<T> {
    options: ComboboxOption<T>[]
    /** id of the option to mark `aria-selected` (and pass `selected` to renderOption). */
    selectedId?: string | undefined
    onSelect: (value: T, option: ComboboxOption<T>) => void
    renderOption: (option: ComboboxOption<T>, state: { active: boolean; selected: boolean }) => React.ReactNode
    /**
     * Render the whole input area (icons, the <input>, a clear button, an sr-only
     * status…). Spread `getInputProps()` on the <input> and attach `ref`; wire your
     * own value/onChange/onFocus/onClick/placeholder/readOnly/className.
     */
    renderInput: (props: ComboboxInputRenderProps) => React.ReactNode
    /** Accessible name for the listbox popup. */
    label: string
    /** Controlled open state (the caller owns the open trigger — focus/click/type). */
    open: boolean
    onOpenChange: (open: boolean) => void
    /** 'clamp' stops at the ends; 'circular' wraps past them (default 'clamp'). */
    wrap?: "clamp" | "circular"
    /** With nothing highlighted, Enter selects the first option (typeahead nicety). */
    selectFirstOnEnter?: boolean
    /** Reset the active option to none when this changes (pass the query string). */
    resetKey?: unknown
    /** Custom Escape handler (e.g. clear the query). Defaults to closing the popup. */
    onEscape?: () => void
    /** Extra classes for the floating panel (bg/border/width/positioning). */
    panelClassName?: string
    /** Extra classes for the inner <ul> (max-height/scroll/text sizing). */
    listClassName?: string
    /** Chrome above the option list (e.g. an availability banner). */
    panelHeader?: React.ReactNode
    /** Chrome below the option list (e.g. a "no matches" hint). */
    panelFooter?: React.ReactNode
    /** Rendered in place of the list when there are no options (loading/empty messaging). */
    emptyState?: React.ReactNode
    /** Extra classes for the relative wrapper. */
    className?: string
}

/**
 * Accessible single-select combobox: an editable (or readOnly) input paired with a
 * `role="listbox"` popup, tracked virtually via `aria-activedescendant`. The leading
 * "Any X" / "Current location" item is modelled simply as option index 0. Composes
 * the shared `useDismiss` (outside-click) + `useRovingActiveIndex` (Arrow/Enter math)
 * core, the third role-wrapper alongside `MenuButton` (menu) and `ListboxSelect`
 * (button-triggered listbox). The input owns Escape (so a combobox can clear-on-Escape)
 * and lets Space/Home/End reach the text field, so it disables those in the shared core.
 */
export function Combobox<T>({
    options,
    selectedId,
    onSelect,
    renderOption,
    renderInput,
    label,
    open,
    onOpenChange,
    wrap = "clamp",
    selectFirstOnEnter = false,
    resetKey,
    onEscape,
    panelClassName,
    listClassName,
    panelHeader,
    panelFooter,
    emptyState,
    className,
}: ComboboxProps<T>) {
    const triggerRef = useRef<HTMLInputElement>(null)
    const wrapperRef = useRef<HTMLDivElement>(null)
    const listRef = useRef<HTMLUListElement>(null)
    const baseId = useId()
    const listboxId = `${baseId}-listbox`
    const optionId = (i: number) => `${baseId}-option-${i}`

    const setOpen = useCallback((next: boolean) => onOpenChange(next), [onOpenChange])

    // Outside-click closes; the input owns Escape (see closeOnEscape: false below).
    // Containment is the whole wrapper (icons + labels + input + panel are one widget),
    // so clicking a decorative leading icon doesn't dismiss the popup.
    useDismiss({ open, onClose: () => setOpen(false), triggerRef, panelRef: wrapperRef, closeOnEscape: false })

    const selectByIndex = useCallback((index: number) => {
        const opt = options[index]
        if (!opt || opt.disabled) return
        // The caller's onSelect owns closing (its handler sets the open state false),
        // matching the pre-extraction behaviour of every adopting surface.
        onSelect(opt.value, opt)
    }, [options, onSelect])

    const { activeIndex, setActiveIndex, onKeyDown: rovingKeyDown } = useRovingActiveIndex({
        itemCount: options.length,
        wrap,
        listRef,
        onSelect: selectByIndex,
        homeEnd: false,
        selectOnSpace: false,
    })

    // Drop any highlight when the popup (re)opens or the query changes, so a fresh
    // option set never keeps a stale active index pointing at a now-different option.
    useEffect(() => {
        setActiveIndex(-1)
    }, [open, resetKey, setActiveIndex])

    const onInputKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (!open) return
        if (e.key === "Escape") {
            e.preventDefault()
            if (onEscape) onEscape()
            else setOpen(false)
            return
        }
        if (e.key === "Tab") {
            // Let focus leave naturally, but close the popup behind it.
            setOpen(false)
            return
        }
        if (e.key === "Enter" && activeIndex < 0 && selectFirstOnEnter && options.length > 0) {
            e.preventDefault()
            selectByIndex(0)
            return
        }
        rovingKeyDown(e)
    }

    const getInputProps = useCallback((): React.InputHTMLAttributes<HTMLInputElement> => ({
        role: "combobox",
        "aria-autocomplete": "list",
        "aria-expanded": open,
        // Only reference the listbox while it's actually in the DOM — the <ul> renders
        // only when open with options; empty/loading states show bare chrome instead.
        "aria-controls": open && options.length > 0 ? listboxId : undefined,
        "aria-activedescendant": open && activeIndex >= 0 && activeIndex < options.length ? optionId(activeIndex) : undefined,
        onKeyDown: onInputKeyDown,
        // onInputKeyDown closes over the latest open/activeIndex each render, so a
        // stable identity isn't needed and would risk a stale closure.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }), [open, activeIndex, options.length, listboxId])

    return (
        <div ref={wrapperRef} className={cn("relative", className)}>
            {renderInput({ ref: triggerRef, open, getInputProps })}
            {open && (
                <div className={cn("absolute top-full z-50 mt-2", panelClassName)}>
                    {panelHeader}
                    {options.length > 0 ? (
                        <ul
                            ref={listRef}
                            id={listboxId}
                            role="listbox"
                            aria-label={label}
                            className={listClassName}
                        >
                            {options.map((opt, i) => (
                                <li
                                    key={opt.id}
                                    id={optionId(i)}
                                    role="option"
                                    aria-selected={opt.id === selectedId}
                                    aria-disabled={opt.disabled || undefined}
                                    // Keep DOM focus on the input so aria-activedescendant + typing stay live.
                                    onMouseDown={(e) => e.preventDefault()}
                                    onClick={() => selectByIndex(i)}
                                    onMouseMove={() => setActiveIndex(i)}
                                >
                                    {renderOption(opt, { active: i === activeIndex, selected: opt.id === selectedId })}
                                </li>
                            ))}
                        </ul>
                    ) : (
                        emptyState
                    )}
                    {panelFooter}
                </div>
            )}
        </div>
    )
}
